import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { DealsProject, SalesApsService, UnassignedProject } from '@zayan-erp/sales';
import { AnalyticsEvent, AnalyticsService, API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import { ISalesOfficeFloorAgentsDatum, SalesOffice } from '@zayan-erp/home-building';

@Injectable({
  providedIn: 'root'
})
export class SalesOfficeService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);
  baseUrl = this.#apiConfig.baseUrlV2;

  createSalesOffice(salesOffice: any): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/SalesOffice`,
      { showLoader: true, showMessage: true, message: 'Sales office created successfully' },
      salesOffice
    );
  }

  updateSalesOffice(salesOffice: any, id: string): Observable<any> {
    return this.#http.putData(`${this.baseUrl}/SalesOffice/${id}`, salesOffice, {
      showLoader: true,
      showMessage: true,
      message: 'Sales office updated successfully'
    });
  }

  deleteSalesOffice(id: string): Observable<any> {
    return this.#http.deleteData(`${this.baseUrl}/SalesOffice/${id}`, {
      showLoader: true,
      showMessage: true,
      message: 'Sales office deleted successfully'
    });
  }

  getSalesOffice(): Observable<SalesOffice[]> {
    return this.#http.getData(`${this.baseUrl}/SalesOffice`, { showLoader: true, showMessage: false });
  }

  getSalesOfficeById(salesOffice: string): Observable<any> {
    return this.#http.getData(`${this.baseUrl}/SalesOffice/${salesOffice}`, { showLoader: true, showMessage: false });
  }
  getSalesOfficeByOperatingUnits(operatingUnitId: string): Observable<SalesOffice[]> {
    return this.#http.getData(`${this.baseUrl}/SalesOffice/operating-unit/${operatingUnitId}`, {
      showLoader: true,
      showMessage: false
    });
  }
  getSalesOfficeProjects(salesOfficeId: string): Observable<DealsProject[]> {
    return this.#http.getData(`${this.baseUrl}/SalesOffice/${salesOfficeId}/projects`, {
      showLoader: true,
      showMessage: false
    });
  }

  unAssignedSalesOfficeProjects(): Observable<UnassignedProject[]> {
    return this.#http.getData(`${this.baseUrl}/lookup/projects?unassignedToSalesOfficeOnly=true`, {
      showLoader: true,
      showMessage: false
    });
  }
  getSalesOfficeFloorAgents(projectIDs: string[]): Observable<ISalesOfficeFloorAgentsDatum[]> {
    return this.#http.postData(
      `${this.baseUrl}/lookup/sales-users`,
      {
        showLoader: true,
        showMessage: false
      },
      projectIDs
    );
  }

  getSalesOfficeFloorAgentsByDealId(dealId: string): Observable<ISalesOfficeFloorAgentsDatum[]> {
    return this.#http.getData(`${this.baseUrl}/deals/${dealId}/sales-floor-agents`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateFloorAgentsByDealId(dealId: string, userId: string): Observable<any> {
    return this.#http
      .putData(
        `${this.baseUrl}/deals/${dealId}/sales-floor-agent`,
        { userId },
        {
          showLoader: true,
          showMessage: true,
          message: 'Floor agent updated successfully'
        }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealFloorAgentUpdated, {
            ...this.#salesAps.dealCtx(dealId),
            floor_agent_user_id: userId
          });
        })
      );
  }

  updateCflStatus(dealId: string): Observable<any> {
    return this.#http
      .putData(
        `${this.baseUrl}/deals/${dealId}/toggle-cfl`,
        {},
        {
          showLoader: true,
          showMessage: true,
          message: 'CFL status updated successfully'
        }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealCflStatusToggled, this.#salesAps.dealCtx(dealId));
        })
      );
  }
}
